import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { TableDropdown } from "components/dropdowns/TableDropdown"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { TablePagination } from "components/paginations/TablePagination"

import { useTimezone } from "hooks/useTimezone"

import { tablePageSize } from "constants/data"

import { balanceService } from "services/balance/balance.service"
import { PaymentRoute } from "services/balance/type"
import { Paginated } from "services/tags/type"

import { CreatePaymentRouteModal } from "./CreatePaymentRouteModal"

type Props = {
  loading: boolean
  setLoading: (v: boolean) => void
}

const DEFAULT_PAGE_SIZE = 5

export const PaymentRoutesPanel: FC<Props> = ({ loading, setLoading }) => {
  const { formatDateTime } = useTimezone()
  const [result, setResult] = useState<Paginated<PaymentRoute> | null>(null)
  const [openCreate, setOpenCreate] = useState(false)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [processor, setProcessor] = useState<string>("")
  const [processors, setProcessors] = useState<string[]>([])

  const loadProcessors = useCallback(async () => {
    const res = await balanceService.listPaymentRouteProcessors()
    setProcessors(res ?? [])
  }, [])

  const load = useCallback(async () => {
    const res = await balanceService.listPaymentRoutes({ page, pageSize, ...(processor ? { processor } : {}) })

    if (res) setResult(res)
  }, [page, pageSize, processor])

  useEffect(() => {
    loadProcessors().catch((e: any) => toast.error(e?.data?.message ?? e?.message ?? "Failed to load processors"))
  }, [loadProcessors])

  useEffect(() => {
    setLoading(true)
    load()
      .catch((e: any) => toast.error(e?.data?.message ?? e?.message ?? "Failed to load routes"))
      .finally(() => setLoading(false))
  }, [load, setLoading])

  const onCreated = useCallback(async () => {
    setOpenCreate(false)
    toast.success("Route created")
    setLoading(true)
    try {
      await load()
    } finally {
      setLoading(false)
    }
  }, [load, setLoading])

  const onJump = (jumpTo: number) => {
    if (!jumpTo) return
    setPage(jumpTo)
  }
  const onPageSizeChange = (value: number): void => {
    setPageSize(value)
  }

  const onProcessorChange = (value: string) => {
    setProcessor(value)
    setPage(1)
  }

  const processorOptions = useMemo(() => (processors ?? []).map((p) => ({ name: p, value: p })), [processors])

  const rows = useMemo(() => result?.data ?? [], [result])
  const totalPages = result?.totalPages ?? 1

  const tableHead = "text-xs uppercase tracking-wide text-[rgba(6,7,21,0.6)] dark:text-white/70"
  const cell = "py-3 px-3 text-sm text-[rgba(6,7,21,1)] dark:text-white"

  return (
    <div className={clsx("rounded-xl border p-4", "border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]")}>
      <div className="flex flex-col items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">Payment Routes</div>
          <div className="mt-1 text-sm text-[rgba(6,7,21,0.7)] dark:text-white/70">
            Auto-discovered routes + manual routes created by admins.
          </div>
        </div>

        <div className="flex flex-row justify-between gap-4 w-full flex-wrap">
          <div className=" min-w-[200px]">
            <DropdownMenu
              rounded
              value={processor}
              disabled={loading}
              placeholder="All Processors"
              options={processorOptions}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                onProcessorChange(e.target.value)
              }}
            />
          </div>

          <div className="flex flex-row gap-2">
            <div className="flex items-center shrink-0 gap-2 text-sm sm:text-base text-black dark:text-white">
              <label>Rows per page:</label>
              <TableDropdown value={pageSize} onValueChange={onPageSizeChange} columns={tablePageSize} />
            </div>

            <button
              className={clsx(
                "rounded-lg shrink-0 px-4 py-2 text-sm font-semibold transition",
                "bg-[rgba(139,92,246,1)] text-white hover:bg-[rgba(139,92,246,0.9)]",
                loading && "opacity-60 cursor-not-allowed"
              )}
              onClick={() => setOpenCreate(true)}
              disabled={loading}
            >
              + Create route
            </button>
          </div>
        </div>
      </div>

      <div className={clsx("mt-4 overflow-x-auto rounded-xl border", "border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]")}>
        <table className="w-full min-w-[900px]">
          <thead className="bg-[rgba(139,92,246,0.06)] dark:bg-[rgba(139,92,246,0.08)]">
            <tr className={tableHead}>
              <th className="py-3 px-3 text-left">Processor</th>
              <th className="py-3 px-3 text-left">Method</th>
              <th className="py-3 px-3 text-left">Terminal</th>
              <th className="py-3 px-3 text-left">Source</th>
              <th className="py-3 px-3 text-left">Active</th>
              <th className="py-3 px-3 text-left">First seen</th>
              <th className="py-3 px-3 text-left">Last seen</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.08)]">
                <td className={cell}>{r.processor}</td>
                <td className={cell}>{r.payment_method}</td>
                <td className={cell}>{r.terminal_name}</td>
                <td className={cell}>
                  <span
                    className={clsx(
                      "rounded-md px-2 py-1 text-xs font-semibold",
                      r.source === "AUTO"
                        ? "bg-[rgba(34,197,94,0.12)] text-[rgba(21,128,61,1)] dark:text-[rgba(134,239,172,1)]"
                        : "bg-[rgba(59,130,246,0.12)] text-[rgba(29,78,216,1)] dark:text-[rgba(147,197,253,1)]"
                    )}
                  >
                    {r.source}
                  </span>
                </td>
                <td className={cell}>
                  {r.is_active ? (
                    <span className="rounded-md bg-[rgba(34,197,94,0.12)] px-2 py-1 text-xs font-semibold text-[rgba(21,128,61,1)] dark:text-[rgba(134,239,172,1)]">
                      Yes
                    </span>
                  ) : (
                    <span className="rounded-md bg-[rgba(239,68,68,0.12)] px-2 py-1 text-xs font-semibold text-[rgba(185,28,28,1)] dark:text-[rgba(252,165,165,1)]">
                      No
                    </span>
                  )}
                </td>
                <td className={cell}>{r.first_seen_at ? formatDateTime(r.first_seen_at) : "—"}</td>
                <td className={cell}>{r.last_seen_at ? formatDateTime(r.last_seen_at) : "—"}</td>
              </tr>
            ))}

            {!rows.length && (
              <tr>
                <td colSpan={7} className="py-10 text-center text-sm text-[rgba(6,7,21,0.6)] dark:text-white/60">
                  No routes yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <TablePagination page={page} totalPages={totalPages} onJump={onJump} />
      </div>

      <CreatePaymentRouteModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={onCreated}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  )
}
